import type {ActionPlan, ActionResult} from './actionCardTypes';
import type {Message} from './useAIChatStream';

export type ActionCardState = {resolved?: boolean; result?: ActionResult; dismissed?: boolean};

/** The card fields a conversation saved by the single-card build carries. */
export type LegacyActionFields = {
	actionPlan?: ActionPlan;
	actionPaused?: boolean;
	actionResolved?: boolean;
	actionResult?: ActionResult;
	actionDismissed?: boolean;
};

/** A resolve that never reached the gateway, so nothing was recorded for the card. */
export const isTransportFailure = (result: ActionResult): boolean => result.status === 'failed' && result.guard === 'transport';

const holdsAction = (message: Message, actionId: string): boolean =>
	message.actionPlans?.some(p => p.action_id === actionId) === true;

export function findActionPlan(msgs: Message[], actionId: string): ActionPlan | undefined {
	for (const m of msgs) {
		const plan = m.actionPlans?.find(p => p.action_id === actionId);
		if (plan) return plan;
	}
	return undefined;
}

export function indexOfActionMessage(msgs: Message[], actionId: string): number {
	return msgs.findIndex(m => holdsAction(m, actionId));
}

export function actionStateOf(message: Message, actionId: string): ActionCardState {
	return message.actionStates?.[actionId] ?? {};
}

export function attachActionPlan(message: Message, plan: ActionPlan): Message {
	if (holdsAction(message, plan.action_id)) return message;
	return {...message, actionPlans: [...(message.actionPlans ?? []), plan]};
}

const patchActionState = (msgs: Message[], actionId: string, patch: ActionCardState): Message[] =>
	msgs.map(m =>
		holdsAction(m, actionId)
			? {...m, actionStates: {...m.actionStates, [actionId]: {...actionStateOf(m, actionId), ...patch}}}
			: m
	);

export function applyActionResult(msgs: Message[], actionId: string, result: ActionResult): Message[] {
	return patchActionState(msgs, actionId, {resolved: true, result});
}

export function dismissAction(msgs: Message[], actionId: string): Message[] {
	return patchActionState(msgs, actionId, {dismissed: true});
}

export function normalizePersistedMessage(stored: Message & LegacyActionFields): Message {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const {actionPlan, actionPaused: _paused, actionResolved, actionResult, actionDismissed, ...rest} = stored;
	if (rest.actionPlans || !actionPlan) return rest;
	const state: ActionCardState = {};
	if (actionResolved !== undefined) state.resolved = actionResolved;
	if (actionResult !== undefined) state.result = actionResult;
	if (actionDismissed !== undefined) state.dismissed = actionDismissed;
	return {
		...rest,
		actionPlans: [actionPlan],
		...(Object.keys(state).length > 0 ? {actionStates: {[actionPlan.action_id]: state}} : {}),
	};
}
